import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { FormInputs } from "../../../../../../types/walletTypes";
import { savedAsiaPayoutData } from "../../../../redux/formDataSlice";
import { getCnyPayoutDocument, setCnyPayoutDocument } from "../asiaPayoutDocument";
import {
  ContentWrapper,
  InputWrapper,
  ItemWrapper,
  Error,
  ButtonsWrapper,
} from "../../../shared/styles";
import { Input } from "../../../../../../components/common/Input";
import { InputPhone } from "../../../../../../components/common/InputPhone";
import { Button } from "../../../../../../components/common/Button";
import { DocumentUpload } from "../../../../components/DocumentUpload";
import Loader from "../../../../../../components/common/Loader";

interface UnionPaySenderFormProps {
  handleNext?: () => void;
  handlePrevious?: () => void;
}

const UnionPaySenderForm: React.FC<UnionPaySenderFormProps> = ({
  handleNext,
  handlePrevious,
}) => {
  const dispatch = useAppDispatch();
  const asiaPayoutData = useAppSelector((state) => (state.formData as any)?.asiaPayoutData?.cn || {});
  const { loading } = useAppSelector((state) => state.wallets || {});

  const [documentFile, setDocumentFile] = useState<File | null>(
    asiaPayoutData?.uploadedFileName ? getCnyPayoutDocument() : null
  );
  const [uploadedFileName, setUploadedFileName] = useState<string | undefined>(
    asiaPayoutData?.uploadedFileName
  );

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors, isValid },
  } = useForm<FormInputs>({
    defaultValues: asiaPayoutData,
    mode: "onChange",
  });

  useEffect(() => {
    if (asiaPayoutData && Object.keys(asiaPayoutData).length > 0) {
      reset({ ...asiaPayoutData });
      if (asiaPayoutData.uploadedFileName) {
        setUploadedFileName(asiaPayoutData.uploadedFileName);
      }
    }
  }, [asiaPayoutData, reset]);

  const handleFileUpload = (file: File | null) => {
    setCnyPayoutDocument(file);
    setDocumentFile(file);
    setUploadedFileName(file?.name);
    setValue("uploadedFileName", file?.name);
  };

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    const updatedData: FormInputs = {
      ...asiaPayoutData,
      ...data,
      uploadedFileUrl: undefined,
      documentUrl: undefined,
      uploadedFileName,
    };
    dispatch(savedAsiaPayoutData({ country: "cn", data: updatedData }));
    handleNext?.();
  };

  const isDocUploaded = !!documentFile;
  const disableButton = !isValid || !isDocUploaded;

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      <ItemWrapper>
        <InputWrapper>
          <Controller
            name="senderFirstName"
            control={control}
            rules={{ required: "First name is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="First name"
                value={field.value || ""}
                onChange={field.onChange}
                hasError={!!errors?.senderFirstName}
              />
            )}
          />
          {errors?.senderFirstName?.message && (
            <Error>{String(errors.senderFirstName.message)}</Error>
          )}
        </InputWrapper>

        <InputWrapper>
          <Controller
            name="senderLastName"
            control={control}
            rules={{ required: "Last name is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="Last name"
                value={field.value || ""}
                onChange={field.onChange}
                hasError={!!errors?.senderLastName}
              />
            )}
          />
          {errors?.senderLastName?.message && (
            <Error>{String(errors.senderLastName.message)}</Error>
          )}
        </InputWrapper>
      </ItemWrapper>

      <InputWrapper>
        <Controller
          name="senderPhone"
          control={control}
          rules={{ required: "Phone number is required" }}
          render={({ field }) => (
            <InputPhone
              label="Phone number"
              field={field}
              defaultCountry="CN"
            />
          )}
        />
        {errors?.senderPhone?.message && (
          <Error>{String(errors.senderPhone.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <DocumentUpload
          onFileChange={(file) => handleFileUpload(file)}
          showExtra
          mainText="Upload ID"
          subtitle="PDF, JPEG or PNG less than 5MB"
          uploadedFileName={documentFile ? uploadedFileName : undefined}
        />
      </InputWrapper>

      <ButtonsWrapper>
        <Button onClick={handlePrevious} theme="alternate" height="48px" width="203px">
          Back
        </Button>
        <Button onClick={handleSubmit(onSubmit)} disabled={disableButton} label="Continue" height="48px" />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default UnionPaySenderForm;
